import { Col, H2, Paragraph, Row } from '@aurora/widgets-react';
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  FunctionComponent,
} from 'react';
import { Map } from 'react-leaflet';
import useMediaQuery from 'use-media-query-hook';
import { orAbove } from '../../common/breakpointQueries';
import { useHashParams } from '../../services/hooks/useHashParam';
import { SatelliteMap } from '../MapViewer';
import { SATELLITE_GROUPS } from '../MapViewer/MapConfig';
import { TimedGroup } from '../MapViewer/TimedGroup';
import { useTimedLayerGroups } from '../MapViewer/useTimedLayerGroups';
import { SurfaceTemperatureLegend } from './SurfaceTemperatureLegend';
import { useTranslation } from 'react-i18next';

export interface SatelliteProps {
  mapRef: MutableRefObject<Map | null>;
}

export const Satellite: FunctionComponent<SatelliteProps> = ({ mapRef }) => {
  const { t } = useTranslation();

  const {
    times,
    setTimeIndex: innerSetTimeIndex,
    timeIndex,
    setLayerGroup: innerSetLayerGroup,
    layerGroup,
    capabilities,
  } = useTimedLayerGroups(SATELLITE_GROUPS);

  const setTimeIndex = useCallback(
    (value) => {
      innerSetTimeIndex(value);
    },
    [innerSetTimeIndex]
  );

  const { hashParams, setHashParams } = useHashParams();

  useEffect(() => {
    if (hashParams.sat) {
      const group = SATELLITE_GROUPS[hashParams.sat];
      if (group) {
        innerSetLayerGroup(group);
      }
    }
  }, [hashParams, innerSetLayerGroup]);

  const setLayerGroup = useCallback(
    (value: TimedGroup) => {
      const index = SATELLITE_GROUPS.indexOf(value);
      setHashParams({
        sat: index.toString(),
      });
    },
    [innerSetLayerGroup, setHashParams]
  );

  const isDesktop = useMediaQuery(orAbove.l);
  const isLargeTablet = useMediaQuery(orAbove.s);

  return (
    <>
      <Row>
        <Col l={12}>
          <SatelliteMap
            layerGroups={SATELLITE_GROUPS}
            {...{
              mapRef,
              times,
              layerGroup,
              setLayerGroup,
              timeIndex,
              setTimeIndex,
              capabilities,
            }}
          />
          {layerGroup.key === 'ice' ? (
            <>
              <H2>{t('radarImageOfIce')}</H2>
              <Paragraph>{t('radarImageOfIceParagraph')}</Paragraph>
              <Paragraph>{t('radarImageOfIceParagraph2')}</Paragraph>
              <Paragraph>{t('radarImageOfIceParagraph3')}</Paragraph>
              <Paragraph>{t('radarImageOfIceParagraph4')}</Paragraph>
            </>
          ) : (
            <>
              <H2>{t('surfaceTemperatureLegend')}</H2>
              <SurfaceTemperatureLegend
                layout={isDesktop ? 'l' : isLargeTablet ? 'm' : 's'}
              />
              <Paragraph>{t('surfaceTemperatureExplanation')}</Paragraph>
              <Paragraph>{t('whichSatellitesExplanation')}</Paragraph>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};
